import React, { useContext, useMemo, useRef, useState } from 'react';
import { useAvContext } from '../../../context/AvContextProvider';
import { GENERAL_ENTITY } from './constants';
import { useBridgeEntity } from './hooks/useBridgeEntity';
import { MappingValidationDialog, ValidationMessage, ValidationSeverity } from './MappingValidationDialog';
import { FieldMapping } from './types';
import { isMappingExploded } from './utils';

interface MappingValidationContextProps {
  validate: (mappings: FieldMapping[]) => boolean;
  openValidationDialog: (message: ValidationMessage[]) => void;
  removeValidationMessage: (entity: string, field: string) => void;
  clearValidationMessages: () => void;
  closeValidationDialog: () => void;
  validationMessages: ValidationMessage[];
  isValidationDialogOpen: boolean;
  setValidationContinue: (callback: (() => void) | undefined) => void;
  setValidationClose: (callback: (() => void) | undefined) => void;
}

export const MappingValidationContext = React.createContext({} as MappingValidationContextProps);

export const MappingValidationProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    accountEntities: { oldIngressProjs: entityFields },
  } = useAvContext();
  const { bridgeHiddenFields, isBridgeEntity } = useBridgeEntity();
  const hasCompositeKeyDefinition = (entityName: string) => !!bridgeHiddenFields[entityName];
  const [validationMessages, setValidationMessages] = useState<ValidationMessage[]>([]);
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
  const validationContinueRef = useRef<(() => void) | undefined>(undefined);
  const validationCloseRef = useRef<(() => void) | undefined>(undefined);

  const validateBridgeMappings = (mappings: FieldMapping[]): ValidationMessage[] => {
    const groupedByReplica = mappings
      .filter(({ entityName }) => isBridgeEntity(entityName))
      .reduce(
        (acc, { replicaNumber, entityName, modelFieldName, mappingValue }) => {
          const replica = replicaNumber ?? 0;
          const key = `${mappingValue}:${modelFieldName}`;
          acc[entityName] ??= {};
          acc[entityName][replica] ??= new Set<string>();
          acc[entityName][replica].add(key);
          return acc;
        },
        {} as Record<string, Record<number, Set<string>>>
      );

    return Object.entries(groupedByReplica)
      .filter(([, replicas]) => {
        const signatures = Object.values(replicas).map(keys => Array.from(keys).sort().join('|'));
        return signatures.some((signature, index) => signatures.indexOf(signature) !== index);
      })
      .map(([entityName]) => ({
        type: 'Duplicate Bridge Mapping',
        message: 'At least one replica source must be unique.',
        entity: entityName,
        severity: ValidationSeverity.Error,
      }));
  };

  const validateExplodeMappings = (mappings: FieldMapping[]): ValidationMessage[] => {
    const entityGroups = Object.groupBy(
      mappings.filter(m => !isBridgeEntity(m.entityName) && isMappingExploded(m)),
      m => m.entityName
    );

    return Object.entries(entityGroups)
      .filter(([, group]) => group?.every(m => !m.key))
      .map(([entityName]) => ({
        type: 'Incomplete Field Mapping',
        message: 'Exploded field must be mapped to entity key',
        entity: entityName,
        severity: ValidationSeverity.Error,
        field: entityFields[entityName].keyField,
      }));
  };

  const validate = (mappings: FieldMapping[]) => {
    const entityKeysMap = mappings.reduce((obj, { entityName, key }) => ({ ...obj, [entityName]: obj[entityName] || key }), {});

    const entityWithNoKey = Object.keys(entityKeysMap).filter(
      entity => !(entityKeysMap[entity] || entity === GENERAL_ENTITY || hasCompositeKeyDefinition(entity))
    );

    const validationMessages = [
      ...entityWithNoKey.map(entityName => ({
        type: 'Incomplete Field Mapping',
        message: 'Key field must be mapped for the entity',
        entity: entityName,
        severity: entityName !== 'GlobalVulnerability' ? ValidationSeverity.Error : ValidationSeverity.Warning,
        field: entityFields[entityName].keyField,
      })),
      ...validateBridgeMappings(mappings),
      ...validateExplodeMappings(mappings),
    ];

    if (validationMessages.length > 0) {
      openValidationDialog(validationMessages);
    }
    return !validationMessages.length;
  };

  const openValidationDialog = (messages: ValidationMessage[]) => {
    if (!messages?.length) {
      return;
    }

    setValidationMessages(messages ?? []);
    setIsValidationDialogOpen(true);
  };

  const closeValidationDialog = () => {
    validationCloseRef.current?.();
    setIsValidationDialogOpen(false);
  };

  const continueValidationDialog = () => {
    validationContinueRef.current?.();
    setIsValidationDialogOpen(false);
  };

  const setValidationClose = (callback: (() => void) | undefined) => {
    validationCloseRef.current = callback;
  };

  const setValidationContinue = (callback: (() => void) | undefined) => {
    validationContinueRef.current = callback;
  };

  const removeValidationMessage = (entity: string, field: string) => {
    setValidationMessages(prev => prev.filter(message => message.entity !== entity || message.field !== field));
  };

  const clearValidationMessages = () => {
    setValidationMessages([]);
  };

  const value = useMemo(
    () => ({
      validate,
      openValidationDialog,
      closeValidationDialog,
      validationMessages,
      isValidationDialogOpen,
      setValidationContinue,
      setValidationClose,
      removeValidationMessage,
      clearValidationMessages,
    }),
    [
      validate,
      openValidationDialog,
      closeValidationDialog,
      validationMessages,
      isValidationDialogOpen,
      removeValidationMessage,
      clearValidationMessages,
    ]
  );

  return (
    <MappingValidationContext.Provider value={value}>
      <MappingValidationDialog
        messages={validationMessages}
        isOpen={isValidationDialogOpen}
        onContinue={continueValidationDialog}
        onClose={closeValidationDialog}
      />
      {children}
    </MappingValidationContext.Provider>
  );
};

export const useMappingValidation = () => useContext(MappingValidationContext);
